import { Loader2 } from "lucide-react";
import { formatDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const SHOW_OK = 50;

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

export const ImportCounts = ({ result, loading }) => (
  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg bg-slate-50 px-3 py-2 text-sm" data-testid="import-counts">
    {result ? <>
      <span data-testid="import-count-runs">{plural(result.counts.runs, "run")}</span>
      <span>{plural(result.counts.addresses, "address").replace("addresss", "addresses")}</span>
      <span data-testid="import-count-skipped">{result.counts.skipped} skipped</span>
      <span className={result.counts.problems ? "font-semibold text-rose-700" : "text-emerald-700"} data-testid="import-count-problems">{plural(result.counts.problems, "problem")} left</span>
    </> : <span className="text-slate-500">Checking rows…</span>}
    {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
  </div>
);

const Pick = ({ value, options, placeholder, onChange, testId }) => (
  <Select value={value || "sheet"} onValueChange={(v) => onChange(v === "sheet" ? null : v)}>
    <SelectTrigger className="h-8 min-w-[150px] text-xs" data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>
      <SelectItem value="sheet">{placeholder}</SelectItem>
      {options.map((o) => <SelectItem key={o.id} value={o.id} disabled={o.active === false}>{o.name}{o.active === false ? " (inactive)" : ""}</SelectItem>)}
    </SelectContent>
  </Select>
);

const cell = (item, k, update, testId, wide) => (
  <Input value={item[k]} onChange={(e) => update(item.index, { [k]: e.target.value })} className={`h-8 text-xs ${wide ? "min-w-[220px]" : "min-w-[90px]"}`} data-testid={testId} />
);

const ProblemRow = ({ item, res, result, update }) => (
  <tr className={res.errors.length ? "bg-rose-50" : "bg-amber-50"} data-testid={`import-row-${item.index}`}>
    <td className="px-2 py-2 align-top font-data text-xs">{item.index + 2}</td>
    <td className="px-2 py-2 align-top">{cell(item, "date", update, `import-row-date-${item.index}`)}</td>
    <td className="px-2 py-2 align-top">{cell(item, "start_time", update, `import-row-time-${item.index}`)}</td>
    <td className="px-2 py-2 align-top">{cell(item, "address", update, `import-row-address-${item.index}`, true)}</td>
    <td className="px-2 py-2 align-top">{cell(item, "postcode", update, `import-row-postcode-${item.index}`)}</td>
    <td className="space-y-1 px-2 py-2 align-top">
      {!result.basic && <Pick value={item.locksmith_id} options={result.team} placeholder={item.locksmith ? `As in sheet: ${item.locksmith}` : "Open job"} onChange={(v) => update(item.index, { locksmith_id: v })} testId={`import-row-locksmith-${item.index}`} />}
      <Pick value={item.officer_id} options={result.officers} placeholder={item.officer_name || item.officer_phone ? `As in sheet: ${item.officer_name || item.officer_phone}` : "Pick officer"} onChange={(v) => update(item.index, { officer_id: v })} testId={`import-row-officer-${item.index}`} />
    </td>
    <td className="px-2 py-2 align-top text-xs">
      {res.errors.map((e) => <p key={e} className="text-rose-700" data-testid={`import-row-error-${item.index}`}>{e}</p>)}
      {res.warnings.map((w) => <p key={w} className="text-amber-800">{w}</p>)}
    </td>
    <td className="px-2 py-2 align-top"><Checkbox checked={item.skip} onCheckedChange={(v) => update(item.index, { skip: !!v })} data-testid={`import-row-skip-${item.index}`} /></td>
  </tr>
);

const OkRow = ({ item, res, update }) => (
  <tr className={item.skip ? "text-slate-400 line-through" : ""} data-testid={`import-ok-row-${item.index}`}>
    <td className="px-2 py-1.5 font-data text-xs">{item.index + 2}</td>
    <td className="px-2 py-1.5 text-xs">{res?.date ? formatDate(res.date, "dd/MM/yyyy") : item.date}</td>
    <td className="px-2 py-1.5 text-xs">{res?.start_time || item.start_time}</td>
    <td className="px-2 py-1.5 text-xs">{item.address}</td>
    <td className="px-2 py-1.5 font-data text-xs">{res?.postcode || item.postcode}</td>
    <td className="px-2 py-1.5 text-xs">{res?.locksmith?.name || "Open job"} · {res?.officer?.name}{res?.officer?.kind === "new" && <span className="ml-1 rounded bg-blue-50 px-1 text-blue-800">New officer</span>}</td>
    <td className="px-2 py-1.5 text-xs text-emerald-700">{item.skip ? "Skipped" : "OK"}</td>
    <td className="px-2 py-1.5"><Checkbox checked={item.skip} onCheckedChange={(v) => update(item.index, { skip: !!v })} data-testid={`import-ok-skip-${item.index}`} /></td>
  </tr>
);

const HEAD = ["Row", "Date", "Start", "Address", "Postcode", "Locksmith / officer", "Status", "Skip"];

export const ImportFixStep = ({ items, result, loading, update, onBack, onNext }) => {
  const byIndex = Object.fromEntries((result?.rows || []).map((r) => [r.index, r]));
  const problems = items.filter((i) => !i.skip && (byIndex[i.index]?.errors.length || byIndex[i.index]?.warnings.length));
  const ok = items.filter((i) => !problems.includes(i));
  return (
    <div className="space-y-3" data-testid="import-fix-step">
      <ImportCounts result={result} loading={loading} />
      {result && <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full text-left">
          <thead className="bg-slate-50 text-xs text-slate-500"><tr>{HEAD.map((h) => <th key={h} className="px-2 py-2 font-semibold">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-slate-100">
            {problems.map((i) => <ProblemRow key={i.index} item={i} res={byIndex[i.index]} result={result} update={update} />)}
            {ok.slice(0, SHOW_OK).map((i) => <OkRow key={i.index} item={i} res={byIndex[i.index]} update={update} />)}
          </tbody>
        </table>
        {ok.length > SHOW_OK && <p className="px-3 py-2 text-xs text-slate-500">And {ok.length - SHOW_OK} more rows that are fine.</p>}
      </div>}
      <div className="flex justify-between gap-2">
        <Button variant="outline" onClick={onBack} data-testid="import-fix-back">Back</Button>
        <Button onClick={onNext} disabled={!result || loading || result.counts.problems > 0} className="bg-blue-700 hover:bg-blue-800" data-testid="import-fix-next">
          {result?.counts.problems ? `Fix or skip ${result.counts.problems} problem row${result.counts.problems === 1 ? "" : "s"}` : "Review runs"}
        </Button>
      </div>
    </div>
  );
};
